import React, { useCallback, useEffect, useState } from "react";
import {
  ClerkProvider,
  useClerk,
  SignedIn as ClerkSignedIn,
  SignedOut as ClerkSignedOut,
  SignInButton as ClerkSignInButton,
  SignUpButton as ClerkSignUpButton,
  UserButton as ClerkUserButton,
  RedirectToSignIn as ClerkRedirectToSignIn,
  useAuth as useClerkAuth,
  useUser as useClerkUser,
} from "@clerk/nextjs";
import { captureWarning } from "./infra/error-tracking";
import { B3Size, Button } from "./components/elements";
import { PersonIcon } from "@radix-ui/react-icons";
import { enUS, esES, zhCN } from "@clerk/localizations";
import { getLocale, allSupportedLanguages, Locale } from "./infra/i18n/locale";
import { translate } from "./infra/i18n";
import { nullUser, User, UseAuthHook } from "./auth-types";
export { ClerkSignInButton, ClerkSignUpButton };
import { Plan } from "./user-plan";

// 事件驱动的语言切换函数
export const switchLanguage = (newLocale: string) => {
  // 更新本地存储
  localStorage.setItem('locale', newLocale);

  // 触发自定义事件
  window.dispatchEvent(new CustomEvent('languageChanged', {
    detail: { locale: newLocale }
  }));
};

export const isAuthEnabled = !!process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY;
/* console.log('Environment debug:', {
  key: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
  isAuthEnabled,
  nodeEnv: process.env.NODE_ENV,
  typeof: typeof process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY
}); */

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [locale, setLocale] = useState(getLocale());

  useEffect(() => {
    // 监听语言变化事件而不是轮询
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { locale: newLocale } = customEvent.detail;
      setLocale(newLocale);
    };

    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  const clerkLocalization = locale === "zh" ? zhCN : enUS;
                            // getLocale() === "es" ? esES :

  const handleError = useCallback((error: Error) => {
    captureWarning(error.message);
  }, []);

  // // 临时测试中文，强制使用中文
  // // console.log('Testing zhCN:', zhCN);
  // // const clerkLocalization = zhCN;
  // const clerkLocalization = getLocale() === "zh" ? zhCN : enUS;
  //                           // getLocale() === "zh" ? zhCN : enUS;
  //                           // getLocale() === "es" ? esES :

  if (!isAuthEnabled) {
    return children as JSX.Element;
  }

  return (
    // @ts-expect-error need to fix @types/react https://github.com/reduxjs/react-redux/issues/1886
    <ClerkProvider localization={clerkLocalization} onError={handleError}>
      {children}
    </ClerkProvider>
  );
};

// 新增导出 UserButtonWithLocalization 组件，用于“用户头像”信息的本地化使用
export const UserButtonWithLocalization = () => {
  const clerk = useClerk() as any;
  const [locale, setLocale] = useState(getLocale());

  useEffect(() => {
    if (!clerk?.loaded) return;

    const updateLocalization = (newLocale: string) => {
      const localization = newLocale === "zh" ? zhCN : enUS;
      clerk.__unstable__updateProps({ localization });
    };

    // 监听语言变化事件
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { locale: newLocale } = customEvent.detail;
      setLocale(newLocale);
      updateLocalization(newLocale);
    };

    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [clerk]);

  return <UserButton />;
};
const useAuthWithClerk: UseAuthHook = () => {
  const { isSignedIn, userId, signOut, isLoaded } = useClerkAuth();
  const { user: clerkUser } = useClerkUser();

  const user: User = clerkUser
    ? {
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || "",
        firstName: clerkUser.firstName || undefined,
        lastName: clerkUser.lastName || undefined,
        username: clerkUser.username || clerkUser.primaryEmailAddress?.emailAddress || undefined, // 添加 username 提取逻辑
        plan: (clerkUser.publicMetadata?.userPlan || "free") as Plan,
        getLocale: () => {
          const savedLocale = clerkUser.unsafeMetadata?.locale as Locale;
          return savedLocale && allSupportedLanguages.includes(savedLocale)
            ? savedLocale
            : undefined;
        },
        setLocale: async (locale: Locale) => {
          await clerkUser.update({
            unsafeMetadata: {
              ...clerkUser.unsafeMetadata,
              locale,
            },
          });
        },
      }
    : nullUser;

  return { isSignedIn, isLoaded, userId, user, signOut };
};

const useAuthNull: UseAuthHook = () => {
  return {
    isLoaded: true,
    isSignedIn: false,
    userId: undefined,
    user: nullUser,
    signOut: () => {},
  };
};

export const useAuth = isAuthEnabled ? useAuthWithClerk : useAuthNull;

// 修正：添加 mode="modal" 使登录按钮在应用内渲染
export const SignInButton = ({
  onClick,
  autoFocus = false,
  children,
  forceRedirectUrl,
}: {
  onClick?: () => void;
  autoFocus?: boolean;
  children?: React.ReactNode;
  forceRedirectUrl?: string;
}) => {
  if (!isAuthEnabled) return null;

  return (
    // 关键修正：添加 mode="modal" 强制使用模态框而非重定向，删除 forceRedirectUrl={forceRedirectUrl}
    <ClerkSignInButton mode="modal" forceRedirectUrl={forceRedirectUrl}>
      {!children && (
        <Button
          variant="quiet"
          className="text-purple-500 font-semibold"
          autoFocus={autoFocus}
          onClick={onClick}
        >
          {translate("login")}
        </Button>
      )}
    </ClerkSignInButton>
  );
};

// 修正：添加 mode="modal" 使注册按钮在应用内渲染
export const SignUpButton = ({
  onClick,
  autoFocus = false,
  size = "sm",
}: {
  size?: B3Size | "full-width";
  onClick?: () => void;
  autoFocus?: boolean;
}) => {
  if (!isAuthEnabled) return null;

  return (
    // 关键修正：添加 mode="modal" 强制使用模态框而非重定向
    <ClerkSignUpButton mode="modal">
      <Button
        variant="primary"
        size={size}
        onClick={onClick}
        autoFocus={autoFocus}
      >
        <PersonIcon /> {translate("register")}
      </Button>
    </ClerkSignUpButton>
  );
};
export const SignedIn = ({ children }: { children: React.ReactNode }) => {
  if (!isAuthEnabled) return null;
  return <ClerkSignedIn>{children}</ClerkSignedIn>;
};
export const SignedOut = ({ children }: { children: React.ReactNode }) => {
  if (!isAuthEnabled) return children as JSX.Element;
  return <ClerkSignedOut>{children}</ClerkSignedOut>;
};
export const UserButton = isAuthEnabled
  ? ClerkUserButton
  : () => <button></button>;

export const RedirectToSignIn = isAuthEnabled
  ? ClerkRedirectToSignIn
  : () => {
      return null;
    };
